/**
 * Early (parser-sync) dress name display — avoids catalog title flashing before
 * localStorage names are applied. Loaded as a blocking classic script in <head>.
 */
(function () {
  const PREFIX = 'be_dress_name:';
  const TTL_MS = 7 * 24 * 60 * 60 * 1000;

  /** @param {string} productId @param {string} variantId */
  function dressNameStorageKey(productId, variantId) {
    if (!productId || !variantId) return '';
    return `${PREFIX}${productId}_${variantId}`;
  }

  /** @param {string} productId @param {string} variantId */
  function readStoredDressName(productId, variantId) {
    const key = dressNameStorageKey(productId, variantId);
    if (!key) return '';
    try {
      const raw = localStorage.getItem(key);
      if (raw == null || raw === '') return '';

      if (raw.startsWith('{')) {
        let parsed;
        try {
          parsed = JSON.parse(raw);
        } catch {
          localStorage.removeItem(key);
          return '';
        }
        if (!parsed || typeof parsed !== 'object' || typeof parsed.n !== 'string') {
          localStorage.removeItem(key);
          return '';
        }
        const at = typeof parsed.at === 'number' ? parsed.at : 0;
        if (Date.now() - at > TTL_MS) {
          localStorage.removeItem(key);
          return '';
        }
        return parsed.n.trim();
      }

      const legacy = raw.trim();
      if (!legacy) {
        localStorage.removeItem(key);
        return '';
      }
      try {
        localStorage.setItem(key, JSON.stringify({ n: legacy, at: Date.now() }));
      } catch {
        /* ignore */
      }
      return legacy;
    } catch {
      return '';
    }
  }

  if (typeof window !== 'undefined') {
    const existing = window.__beDressNameStorage || {};
    window.__beDressNameStorage = Object.assign(existing, {
      read: readStoredDressName,
      key: dressNameStorageKey,
    });
  }

  /** @param {string} pid @param {string} vid */
  function readDressName(pid, vid) {
    try {
      const api = window.__beDressNameStorage;
      if (!api || typeof api.read !== 'function') return '';
      const s = api.read(String(pid), String(vid));
      return typeof s === 'string' ? s.trim() : '';
    } catch {
      return '';
    }
  }

  /** @param {Element} node */
  function resolveIds(node) {
    const productId = node.getAttribute('data-dress-name-product-id') || '';
    const source = node.getAttribute('data-dress-name-variant-source') || 'fixed';
    const fixed = node.getAttribute('data-dress-name-variant-id') || '';

    if (source === 'card') {
      const card = node.closest('product-card');
      const checked = card?.querySelector('input[type="radio"][data-variant-id]:checked');
      const vid = checked?.getAttribute('data-variant-id')?.trim();
      if (vid) return { productId, variantId: vid };
      return { productId, variantId: fixed };
    }

    if (source === 'pdp') {
      const form = document.querySelector(`product-form-component[data-product-id="${productId}"]`);
      const inp =
        form?.querySelector('input[name="id"]') ||
        document.querySelector('.product-details input[name="id"]');
      const vid = inp instanceof HTMLInputElement ? inp.value.trim() : '';
      if (vid) return { productId, variantId: vid };
      return { productId, variantId: fixed };
    }

    return { productId, variantId: fixed };
  }

  /** @param {Element} el */
  function applyDressNameToNode(el) {
    if (!(el instanceof Element)) return;

    const catalog = el.getAttribute('data-catalog-title') || '';
    const mode = el.getAttribute('data-dress-name-for') || 'text';
    const targetSel = el.getAttribute('data-dress-name-target');
    const target = targetSel ? el.querySelector(targetSel) : el;
    if (!target) return;

    const { productId, variantId } = resolveIds(el);
    if (!productId || !variantId) return;

    const source = el.getAttribute('data-dress-name-variant-source') || 'fixed';
    const stored = source === 'card' ? '' : readDressName(productId, variantId);
    const display = stored || catalog;

    if (mode === 'aria-label') {
      target.setAttribute('aria-label', display);
    } else {
      target.textContent = display;
    }

    el.setAttribute('data-dress-name-ready', '');
  }

  /** @param {ParentNode} [root] */
  function syncDressNameDisplays(root) {
    const scope = root && 'querySelectorAll' in root ? root : document;
    scope.querySelectorAll('[data-dress-name-display]').forEach((el) => {
      if (el instanceof Element) applyDressNameToNode(el);
    });
  }

  function dispatchSync() {
    syncDressNameDisplays(document);
  }

  window.__beDressNameDisplaySync = dispatchSync;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('[data-dress-name-display]')) applyDressNameToNode(node);
        node.querySelectorAll('[data-dress-name-display]').forEach((el) => {
          if (el instanceof Element) applyDressNameToNode(el);
        });
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dispatchSync);
  } else {
    dispatchSync();
  }
})();
